import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import * as z from 'zod';

import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type { RevenueLockdownCreateSchema, RevenueLockdownUpdateSchema } from './revenue-lockdown-mutation-schema';
import type { RevenueLockdownAllQuerySchema, RevenueLockdownSchema } from './revenue-lockdown-query-schema';
import { revenueLockdownAllQuerySchema, revenueLockdownQuerySchema } from './revenue-lockdown-query-schema';

function withCreatedByInclude(apiParamStr: string) {
  const base = apiParamStr?.trim() || '';
  const joiner = base.includes('?') ? '&' : '?';
  return `${base}${joiner}include=created_by.profile_images`;
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['REVENUE_LOCKDOWNS'] });
};

export function useGetRevenueLockdowns(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['REVENUE_LOCKDOWNS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<RevenueLockdownAllQuerySchema>({
        endpoint: '/api/v1/revenue-lockdowns',
        params: withCreatedByInclude(apiParamStr),
        responseSchema: revenueLockdownAllQuerySchema,
      });
      return res;
    },
  });
}

export function useGetRevenueLockdown(id: string) {
  return useSuspenseQuery({
    queryKey: ['REVENUE_LOCKDOWN', id],
    queryFn: async () => {
      const responseSchema = z.object({ data: revenueLockdownQuerySchema });
      const res = await apiClient.get<{ data: RevenueLockdownSchema }>({
        endpoint: `/api/v1/revenue-lockdowns/${id}`,
        params: { include: 'created_by' },
        preventAuditLog: true,
        responseSchema,
      });

      return res;
    },
  });
}

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, RevenueLockdownCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/revenue-lockdowns/',
        payload,
      }),
    onSuccess: () => {
      invalidateQueryAllObject();
    },
  });

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: RevenueLockdownSchema } | null, ApiError, Partial<RevenueLockdownUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/revenue-lockdowns/${id}`,
        method: 'PUT',
        payload,
      }),
    onSuccess: () => {
      invalidateQueryAllObject();
      queryClient.invalidateQueries({ queryKey: ['REVENUE_LOCKDOWN', id] });
    },
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['REVENUE_LOCKDOWN_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/revenue-lockdowns/${id}`,
        method: 'DELETE',
        payload: {},
      }),
    onSuccess: () => {
      invalidateQueryAllObject();
    },
  });
